import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Upload,
  Users,
  FileType,
  LogOut,
  UserCog,
  FileText,
  Wrench,
  Receipt,
  Package,
  ScrollText,
  FileStack,
  LayoutDashboard,
  ChevronRight,
  Headphones,
  HelpCircle,
  Plug,
  Palette,
  Building2,
  Clock,
  Bell,
  Sparkles,
  ClipboardList,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { DynamicLogo } from '../../components/DynamicLogo';
import { ThemeToggle } from '../../components/ThemeToggle';
import { DashboardHome } from './components/DashboardHome';
import { DocumentUpload } from './components/DocumentUpload';
import { ClientManagement } from './components/ClientManagement';
import { TipoDocumentoManagement } from './components/TipoDocumentoManagement';
import { UserManagement } from './components/UserManagement';
import { PropostaManagement } from './components/PropostaManagement';
import { ProdutoServicoManagement } from './components/ProdutoServicoManagement';
import { ProdutoManagement } from './components/ProdutoManagement';
import { FaturaManagement } from './components/FaturaManagement';
import { ContratoManagement } from './components/ContratoManagement';
import { AtendimentosManagement } from './components/AtendimentosManagement';
import { IntegrationsManagement } from './components/IntegrationsManagement';
import { IdentidadeVisual } from './components/config/IdentidadeVisual';
import { DadosEmpresa } from './components/config/DadosEmpresa';
import { HorarioSuporte } from './components/config/HorarioSuporte';
import { Notificacoes } from './components/config/Notificacoes';
import { LlmProviders } from './components/config/LlmProviders';
import { BriefingManagement } from './components/BriefingManagement';

export function Dashboard() {
  const [activeTab, setActiveTab] = useState('home');
  const { user, isAuthenticated, isAdmin, hasHydrated, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const atendimentoParam = searchParams.get('atendimento');
  const [atendimentoTicketId, setAtendimentoTicketId] = useState<string | null>(atendimentoParam);

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }
    if (!isAuthenticated) {
      window.location.href = '/admin/login';
    } else if (!isAdmin) {
      window.location.href = '/portal';
    }
  }, [hasHydrated, isAuthenticated, isAdmin]);

  // Redirecionamento via query param (?atendimento=<ticketId>): abre a tela do ticket
  useEffect(() => {
    if (atendimentoParam) {
      setAtendimentoTicketId(atendimentoParam);
      setActiveTab('atendimentos');
    }
  }, [atendimentoParam]);

  const handleLogout = () => {
    logout();
    window.location.href = '/admin/login';
  };

  if (!hasHydrated || !isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  const menuSections = [
    {
      title: 'Home',
      items: [{ id: 'home', label: 'Dashboard', icon: LayoutDashboard }],
    },
    {
      title: 'Gestão',
      items: [
        { id: 'documents', label: 'Documentos', icon: Upload },
        { id: 'propostas', label: 'Propostas', icon: FileText },
        { id: 'contratos', label: 'Contratos', icon: ScrollText },
        { id: 'faturas', label: 'Faturas', icon: Receipt },
        { id: 'atendimentos', label: 'Atendimentos', icon: Headphones },
        { id: 'briefings', label: 'Briefings', icon: ClipboardList },
      ],
    },
    {
      title: 'Cadastros',
      items: [
        { id: 'clients', label: 'Clientes', icon: Users },
        { id: 'produtos', label: 'Produtos', icon: Package },
        { id: 'servicos', label: 'Serviços', icon: Wrench },
        { id: 'modelos-contrato', label: 'Modelos Contrato', icon: FileStack },
        { id: 'tipos', label: 'Tipos de Documento', icon: FileType },
      ],
    },
    {
      title: 'Sistema',
      items: [
        { id: 'users', label: 'Usuários', icon: UserCog },
        { id: 'integrations', label: 'Integrações', icon: Plug },
      ],
    },
    {
      title: 'Configurações',
      items: [
        { id: 'config-visual', label: 'Identidade Visual', icon: Palette },
        { id: 'config-empresa', label: 'Dados da Empresa', icon: Building2 },
        { id: 'config-suporte', label: 'Horário de Suporte', icon: Clock },
        { id: 'config-notificacoes', label: 'Notificações', icon: Bell },
        { id: 'config-llm', label: 'APIs de IA', icon: Sparkles },
      ],
    },
  ];

  const allTabs = menuSections.flatMap((s) => s.items);

  return (
    <>
      <Helmet>
        <title>Dashboard Admin</title>
      </Helmet>

      <div className="h-screen bg-background flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 bg-sidebar border-r border-sidebar-border text-sidebar-foreground flex flex-col">
          {/* Logo */}
          <div className="h-20 shrink-0 px-4 flex items-center">
            <DynamicLogo
              area="sidebar"
              alt="Logo"
              className="h-8 w-auto"
              fallback={
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-brand rounded-md flex items-center justify-center">
                    <span className="text-white font-bold text-sm">F</span>
                  </div>
                  <span className="font-semibold">Folio</span>
                </div>
              }
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            {menuSections.map((section) => (
              <div key={section.title} className="mb-4">
                <p className="px-4 mb-2 text-xs font-medium text-sidebar-foreground/50 uppercase tracking-wider">
                  {section.title}
                </p>
                <div className="space-y-0.5 px-2">
                  {section.items.map((item: any) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                        activeTab === item.id
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium shadow-sm'
                          : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/80'
                      }`}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-sidebar-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-sidebar-accent rounded-full flex items-center justify-center">
                <span className="text-sm font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">Administrador</p>
              </div>
            </div>
            <div className="space-y-1">
              <a
                href="/area-cliente"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/80 rounded-md transition-colors"
              >
                <Users className="w-4 h-4" />
                <span>Portal do Cliente</span>
              </a>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-sidebar-foreground/70 hover:text-destructive hover:bg-destructive/20 rounded-md transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="h-20 shrink-0 bg-sidebar border-b border-sidebar-border px-6 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-sidebar-foreground opacity-70">
                {menuSections.find((s) => s.items.some((i) => i.id === activeTab))?.title}
              </span>
              <ChevronRight className="w-4 h-4 text-sidebar-foreground opacity-70" />
              <span className="font-medium text-sidebar-foreground">
                {allTabs.find((t) => t.id === activeTab)?.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/ajuda"
                aria-label="Ajuda"
                title="Central de Ajuda"
                className="p-2 rounded-lg hover:bg-sidebar-accent transition-colors"
              >
                <HelpCircle className="w-5 h-5 text-sidebar-foreground opacity-70" />
              </Link>
              <ThemeToggle variant="dropdown" />
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden bg-muted/30">
            {activeTab === 'home' && <DashboardHome />}
            {activeTab === 'documents' && (
              <div className="p-6">
                <DocumentUpload />
              </div>
            )}
            {activeTab === 'propostas' && (
              <div className="p-6">
                <PropostaManagement
                  onNavigateToClients={() => setActiveTab('clients')}
                />
              </div>
            )}
            {activeTab === 'contratos' && (
              <div className="p-6">
                <ContratoManagement initialView="contratos" />
              </div>
            )}
            {activeTab === 'modelos-contrato' && (
              <div className="p-6">
                <ContratoManagement initialView="modelos" />
              </div>
            )}
            {activeTab === 'faturas' && (
              <div className="p-6">
                <FaturaManagement />
              </div>
            )}
            {activeTab === 'produtos' && (
              <div className="p-6">
                <ProdutoManagement />
              </div>
            )}
            {activeTab === 'servicos' && (
              <div className="p-6">
                <ProdutoServicoManagement onBack={() => setActiveTab('propostas')} />
              </div>
            )}
            {activeTab === 'clients' && (
              <div className="p-6">
                <ClientManagement />
              </div>
            )}
            {activeTab === 'tipos' && (
              <div className="p-6">
                <TipoDocumentoManagement />
              </div>
            )}
            {activeTab === 'users' && (
              <div className="p-6">
                <UserManagement />
              </div>
            )}
            {activeTab === 'config-visual' && (
              <div className="p-6">
                <IdentidadeVisual />
              </div>
            )}
            {activeTab === 'config-empresa' && (
              <div className="p-6">
                <DadosEmpresa />
              </div>
            )}
            {activeTab === 'config-suporte' && (
              <div className="p-6">
                <HorarioSuporte />
              </div>
            )}
            {activeTab === 'config-notificacoes' && (
              <div className="p-6">
                <Notificacoes />
              </div>
            )}
            {activeTab === 'config-llm' && (
              <div className="p-6">
                <LlmProviders />
              </div>
            )}
            {activeTab === 'integrations' && (
              <div className="p-6">
                <IntegrationsManagement />
              </div>
            )}
            {activeTab === 'atendimentos' && <AtendimentosManagement initialTicketId={atendimentoTicketId} />}
            {activeTab === 'briefings' && <BriefingManagement />}
          </main>
        </div>
      </div>
    </>
  );
}
